import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  Box,
  Typography,
  Divider,
  Chip,
  Alert,
  IconButton,
} from '@mui/material';
import { Download as DownloadIcon, Close as CloseIcon } from '@mui/icons-material';
import { ExportOptions } from '../../types';
import { ExportService } from '../../services/exportService';

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  dataType: 'devices' | 'users' | 'audit_logs';
  data: any[];
  title?: string;
}

const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  dataType,
  data,
  title = 'Export Data',
}) => {
  const [format, setFormat] = useState<'csv' | 'excel' | 'pdf'>('csv');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [filename, setFilename] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableFields = ExportService.getAvailableFields(dataType);

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setFormat('csv');
      setSelectedFields(availableFields.map(field => field.value));
      setFilename(`${dataType}_${new Date().toISOString().split('T')[0]}`);
      setError(null);
    }
  }, [open, dataType, availableFields]);

  const handleFieldToggle = (fieldValue: string) => {
    setSelectedFields(prev => 
      prev.includes(fieldValue)
        ? prev.filter(f => f !== fieldValue)
        : [...prev, fieldValue]
    );
  };

  const handleSelectAll = () => {
    setSelectedFields(availableFields.map(field => field.value));
  };

  const handleSelectNone = () => {
    setSelectedFields([]);
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      setError('Please select at least one field to export');
      return;
    }

    if (!filename.trim()) {
      setError('Please enter a filename');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const options: ExportOptions = {
        format,
        fields: selectedFields,
        filename: filename.trim(),
      };

      ExportService.export(data, dataType, options);
      onClose();
    } catch (err) {
      setError('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getFormatDescription = (format: string) => {
    switch (format) {
      case 'csv':
        return 'Comma-separated values file, compatible with Excel and other spreadsheet applications';
      case 'excel':
        return 'Microsoft Excel file format (.xlsx)';
      case 'pdf':
        return 'Portable Document Format, suitable for printing and sharing';
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <IconButton
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Export {data.length} {dataType} records
          </Typography>
        </Box>

        {/* Format Selection */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={format}
              onChange={(e) => setFormat(e.target.value as 'csv' | 'excel' | 'pdf')}
            >
              <MenuItem value="csv">CSV (Comma Separated Values)</MenuItem>
              <MenuItem value="excel">Excel (.xlsx)</MenuItem>
              <MenuItem value="pdf">PDF Document</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {getFormatDescription(format)}
          </Typography>
        </Box>

        {/* Filename */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Filename"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            placeholder={`${dataType}_export`}
            helperText="File extension will be added automatically"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Field Selection */}
        <Box sx={{ mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1">Select Fields to Export</Typography>
            <Box>
              <Button size="small" onClick={handleSelectAll} sx={{ mr: 1 }}>
                Select All
              </Button>
              <Button size="small" onClick={handleSelectNone}>
                Select None
              </Button>
            </Box>
          </Box>

          <FormGroup>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {availableFields.map((field) => (
                <FormControlLabel
                  key={field.value}
                  control={
                    <Checkbox
                      checked={selectedFields.includes(field.value)}
                      onChange={() => handleFieldToggle(field.value)}
                      size="small"
                    />
                  }
                  label={field.label}
                  sx={{ minWidth: '200px' }}
                />
              ))}
            </Box>
          </FormGroup>

          {selectedFields.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Selected fields: {selectedFields.length} of {availableFields.length}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {selectedFields.map(fieldValue => {
                  const field = availableFields.find(f => f.value === fieldValue);
                  return (
                    <Chip
                      key={fieldValue}
                      label={field?.label}
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        {/* Preview Info */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Export Preview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Format: {format.toUpperCase()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Records: {data.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Fields: {selectedFields.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Filename: {filename}.{format === 'excel' ? 'xlsx' : format}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={loading || selectedFields.length === 0}
        >
          {loading ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;
